import _ from 'underscore';
import globalConfig from '@bingads-webui-component-legacy/config/index';
import * as putil from '@bingads-webui-universal/primitive-utilities';
import preferenceService from '@bingads-webui-component-legacy/preference/service/index';
import {
  initialize, updateEntitySchemaForSOV,
  updateEntitySchemaForRemovedPerformanceMetrics,
  enableForAudienceSOVFeatures,
} from '@bingads-webui-campaign/common-grid-config';

initialize({ globalConfig, preferenceService });

export { updateEntitySchemaForSOV, updateEntitySchemaForRemovedPerformanceMetrics, enableForAudienceSOVFeatures };

const Permissions = globalConfig.get('Permissions');

// get whether feature is enabled by property name
function getFeaturePermissionByPropertyName(propertyName) {
  switch (propertyName.toLowerCase()) {
  case 'campaigntype':
    return !Permissions.IsEnhancedShoppingCampaignAllowed &&
      !Permissions.IsDynamicSearchAdsV1Enabled &&
      !Permissions.IsAIMCampaignEnabled;

  case 'associationscount':
  case 'trackingstatus':
    return Permissions.IsAudienceGridNewColumnsSupported;

  case 'audiencetrackingstatus':
    return Permissions.IsAIMCampaignEnabled ||
      Permissions.IsRemarketingSimilarAudienceAllowed ||
      (Permissions.IsCustomerHierarchyEnabled && Permissions.IsSharedLibraryPhase1Enabled);

  case 'aimreach':
    return Permissions.IsAudienceListSizeEnabled;

  case 'isexperiment':
    return Permissions.IsExperimentsEnabled &&
      // only enable experiment filter in account scope
      !globalConfig.get('CurrentCampaign');

  case 'ismainconversiongoal':
    return true; // [hagu1]todo: skip this method for 'ismainconversiongoal'

  default:
    return false;
  }
}

function removeEnumValuesFromProperty(schema, propertyName, removeEnumArray, depth = 0) {
  if (depth > 1) {
    return schema;
  }

  if (!_.isEmpty(schema.properties[propertyName])) {
    schema.properties[propertyName].enum = _.difference(schema.properties[propertyName].enum, removeEnumArray);
  }

  _.each(schema.properties, (property) => {
    if (_.has(property, 'schema')) {
      removeEnumValuesFromProperty(property.schema, propertyName, removeEnumArray, depth + 1);
    }
  });

  return schema;
}

function updateEnumValueSets(schema) {
  const entityName = schema.name.toLowerCase();

  // Listing here all the enum valus to remove in different pilots (or always remove)
  const disabledTargets = [
    {
      disabled: !Permissions.IsDynamicSearchAdsV1Enabled && (entityName === 'ad'),
      property: 'Type',
      enumValueSets: ['DynamicSearch'], // this will not used in any where in filter options
    },
    {
      disabled: !Permissions.IsDynamicSearchAdsV1Enabled && (entityName === 'campaign'),
      property: 'CampaignType',
      enumValueSets: ['DynamicSearchAds'], // remove dynamic search ads from other entity
    },
    {
      // Bug 1692445: <AD-Mar-19>BetaProd: [Campaigns] Some check box conditions are not shown for filter of Campaign status/Campaign type/Bid strategy type on prod.
      // This should be controlled by pilot
      disabled: !Permissions.IsSmartCampaignPhase2Enabled && (entityName === 'campaign'),
      property: 'CampaignType',
      enumValueSets: ['Smart'],
    },
    {
      disabled: entityName === 'campaign' && !Permissions.IsSmartCampaignPhase2Enabled,
      property: 'Status',
      enumValueSets: ['InProgress'], // remove this when there are localized strings for this enum value
    },
    {
      disabled: true,
      property: 'Delivery',
      enumValueSets: ['CampaignInProgress'],
    },
    {
      disabled: !Permissions.IsSmartCampaignPhase2Enabled,
      property: 'DeliveryStatus',
      enumValueSets: ['CampaignInProgress'],
    },
    {
      disabled: true,
      property: 'Type',
      enumValueSets: ['HotelBookingGoal'],
    },
    {
      disabled: true,
      property: 'DisplayBiddingStrategyType',
      enumValueSets: ['InheritFromParent', 'MaxRoas', 'TargetCostPerSale'], // this will not used in any where in filter options
    },
    {
      disabled: !(Permissions.IsCampaignTargetROASBidStrategyEnabled ||
        Permissions.IsNewCampaignTargetROASBidStrategyEnabled),
      property: 'DisplayBiddingStrategyType',
      enumValueSets: ['TargetRoas'],
    },
    {
      disabled: !Permissions.IsMaximizeConversionValueEnabled && !Permissions.IsSmartShoppingCampaignEnabled,
      property: 'DisplayBiddingStrategyType',
      enumValueSets: ['MaxConversionValue'],
    },
    {
      disabled: !Permissions.IsCostPerSaleEnabled,
      property: 'DisplayBiddingStrategyType',
      enumValueSets: ['CostPerSale'],
    },
    {
      disabled: !Permissions.IsTargetImpressionShareEnabled,
      property: 'DisplayBiddingStrategyType',
      enumValueSets: ['TargetImpressionShare'],
    },
    {
      disabled: !Permissions.IsAIMCampaignEnabled && (entityName === 'campaign'),
      property: 'CampaignType',
      enumValueSets: ['Audience'], // remove audience campaign from other entity
    },
    {
      disabled: !(Permissions.IsCustomerHierarchyEnabled && Permissions.IsSharedLibraryPhase1Enabled),
      property: 'AudienceTrackingStatus',
      enumValueSets: ['InactiveDueToTagUnavailable'],
    },
    {
      disabled: !Permissions.IsAdsGlobalizationPhase1Enabled,
      property: 'Language',
      enumValueSets: ['Greek', 'Polish', 'Bulgarian', 'Croatian', 'Czech', 'Estonian', 'Hungarian', 'Latvian', 'Lithuanian', 'Maltese', 'Romanian', 'Slovak', 'Slovenian'],
    },
    {
      disabled: !Permissions.IsAdsGlobalizationPhase2Enabled,
      property: 'Language',
      enumValueSets: ['Turkish', 'Serbian', 'Bosnian', 'Albanian', 'Macedonian', 'Icelandic'],
    },
    {
      disabled: !Permissions.IsAdsGlobalizationPhase6Enabled,
      property: 'Language',
      enumValueSets: ['Arabic', 'Hebrew', 'Russian'],
    },
    {
      disabled: !Permissions.IsAdsGlobalizationPhase2JapanEnabled,
      property: 'Language',
      enumValueSets: ['Japanese'],
    },
    {
      disabled: !Permissions.IsAdsGlobalizationPhase9Enabled,
      property: 'Language',
      enumValueSets: ['Hindi', 'Malay', 'Thai', 'Indonesian', 'Tagalog'],
    },
    {
      disabled: !Permissions.IsAdsGlobalizationPhase9VIEnabled,
      property: 'Language',
      enumValueSets: ['Vietnamese'],
    },
  ];

  _.chain(disabledTargets)
    .filter({disabled: true})
    .each((target) => {
      removeEnumValuesFromProperty(schema, target.property, target.enumValueSets);
    });
}

export function enableForExperimentFeature(schema) {
  const isInPilot = Permissions.IsExperimentsEnabled;
  const campaignProperty = schema.properties.Campaign;
  const isExperimentProperty = putil.get(campaignProperty, 'schema.properties.IsExperiment');

  if (isInPilot && campaignProperty && isExperimentProperty) {
    isExperimentProperty.filterable = true;
  }

  return schema;
}

export function enableForMainGoalSelectionFeature(schema) {
  const performanceMetricsProperty = schema.properties.PerformanceMetrics;
  const allConvMetrics = [
    'AllConversionCPA',
    'AllConversions',
    'AllConversionRate',
    'AllConversionAdvertiserReportedRevenue',
    'AllConversionRevenueOnAdSpend',
  ];

  _.each(allConvMetrics, (allConvMetric) => {
    if (performanceMetricsProperty && putil.get(performanceMetricsProperty.schema.properties, allConvMetric)) {
      schema.properties.PerformanceMetrics.schema.properties[allConvMetric].filterable = true;
    }
  });

  return schema;
}

export function enableForViewThroughConversionsFeature(schema) {
  const {
    IsViewThroughConversionEnabled,
    IsViewThroughConversionsRevenueEnabled,
  } = Permissions;
  const performanceMetricsProperty = schema.properties.PerformanceMetrics;

  const viewThroughConvMetrics = [
    'ViewThroughConversions',
  ];

  _.each(viewThroughConvMetrics, (viewThroughConvMetric) => {
    if (performanceMetricsProperty && putil.get(performanceMetricsProperty.schema.properties, viewThroughConvMetric)) {
      schema.properties.PerformanceMetrics.schema.properties[viewThroughConvMetric].filterable = IsViewThroughConversionEnabled || false;
    }
  });

  const viewThroughConvRevenueMetrics = [
    'ViewThroughConversionsRevenue',
    'ViewThroughConversionsCPA',
    'ViewThroughConversionsReturnOnAdSpend',
    'ViewThroughConversionsRate',
  ];

  _.each(viewThroughConvRevenueMetrics, (metric) => {
    if (performanceMetricsProperty && putil.get(performanceMetricsProperty.schema.properties, metric)) {
      schema.properties.PerformanceMetrics.schema.properties[metric].filterable = IsViewThroughConversionsRevenueEnabled || false;
    }
  });

  return schema;
}

function enableFilterForPilotProperties(schema, propertyNameArray) {
  _.each(propertyNameArray, (propertyName) => {
    if (getFeaturePermissionByPropertyName(propertyName) && !_.isEmpty(schema.properties[propertyName])) {
      schema.properties[propertyName].filterable = true;
    }
  });

  return schema;
}


export function updateFilterSchemaForPilotFeatures(originalSchema) {
  const schema = putil.cloneDeep(originalSchema);

  // first enable properties (filter type) for features in pilot
  updateEntitySchemaForSOV(schema, false);
  updateEntitySchemaForRemovedPerformanceMetrics(schema, false);
  enableForExperimentFeature(schema);
  enableForMainGoalSelectionFeature(schema);
  enableForViewThroughConversionsFeature(schema);
  enableForAudienceSOVFeatures(schema);
  enableFilterForPilotProperties(schema, ['CampaignType', 'AssociationsCount', 'TrackingStatus', 'AudienceTrackingStatus', 'AIMReach', 'IsExperiment', 'IsMainConversionGoal']);

  // enable enum values (filter value set) for features in pilot
  updateEnumValueSets(schema);
  return schema;
}
